import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Timer, Loader2, AlertCircle, UserCircle2, RefreshCw } from "lucide-react";

/**
 * v23.21 — Admin live shifts panel. Shows every driver currently on-shift
 * with a live timer since clock-in. Auto-refreshes every 30s and the local
 * timers tick every second.
 */
function fmtDuration(secs) {
  if (!secs && secs !== 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtDurationHMS(secs) {
  if (!secs && secs !== 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

export default function AdminLiveShiftsPanel() {
  const [active, setActive] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [now, setNow] = React.useState(Date.now());

  const load = React.useCallback(async (isBackground = false) => {
    if (isBackground) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/shifts/active");
      setActive(data.active || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load live shifts");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Local tick for live timers
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Background refresh every 30s so newly clocked-in drivers appear
  React.useEffect(() => {
    const t = setInterval(() => load(true), 30_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <section className="mb-12" data-testid="admin-live-shifts-panel">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] inline-flex items-center gap-2">
            <Timer size={20} /> Live shifts
          </h2>
          <p className="mt-1 text-sm text-[#475569]">
            Drivers currently clocked in. Live-refreshes every 30s.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="admin-live-shifts-refresh"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-bold text-[#1E4D55] hover:text-[#163C42] disabled:opacity-60"
        >
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Refresh · {active.length} on shift
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="admin-live-shifts-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <div data-testid="admin-live-shifts-loading" className="text-[#475569] text-sm inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : active.length === 0 ? (
        <div data-testid="admin-live-shifts-empty" className="bg-white border border-[#0A0A0A]/10 border-dashed p-8 text-center text-sm text-[#475569]">
          No drivers are on shift right now.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-live-shifts-list">
          {active.map((s) => {
            const startMs = s.started_at ? new Date(s.started_at).getTime() : now;
            const liveSecs = Math.max(0, Math.floor((now - startMs) / 1000));
            const overLimit = liveSecs >= 12 * 3600;
            return (
              <div
                key={s.id}
                data-testid={`admin-shift-row-${s.id}`}
                className={`bg-white border p-4 flex items-center gap-4 flex-wrap ${overLimit ? "border-[#F5A623]/60 bg-[#F5A623]/5" : "border-[#0A0A0A]/10"}`}
              >
                <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#5BB247] animate-pulse" />
                <UserCircle2 size={22} className="text-[#1E4D55]" />
                <div className="flex-1 min-w-[180px]">
                  <p className="font-semibold text-[#0A0A0A] text-sm" data-testid={`admin-shift-name-${s.id}`}>
                    {s.driver_name || "Driver"}
                  </p>
                  <p className="text-xs text-[#475569]">
                    Since {s.started_at ? new Date(s.started_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }) : "—"}
                  </p>
                </div>
                <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]" data-testid={`admin-shift-timer-${s.id}`}>
                  {fmtDurationHMS(liveSecs)}
                </p>
                {overLimit && (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#A66800] border border-[#F5A623]/40 bg-[#F5A623]/10 px-1.5 py-0.5">
                    Long shift
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
